import {useUser} from "../../../Context/UserContext";
import {MessageCodes} from "../MessageCodes";
import ResultText from "../ResultText";
import EmptyLine from "../../EmptyLine";
import ResultTable from "../../Table/ResultTable";
import ResultHeader from "../../Table/ResultHeader";
import ResultRow from "../../Table/ResultRow";
import ResultHeaderCell from "../../Table/ResultHeaderCell";
import ResultBody from "../../Table/ResultBody";
import HecRasHelper from "../../../Helpers/HecRasHelper";
import ResultBodyCell from "../../Table/ResultBodyCell";
import SuperText from "../SuperText";
import StationElevationPlot from "../UI/StationElevationPlot";
import ResultView from "../../V2/ResultView";
import CheckCSName from "../../CheckCSName";

const Footnote = () => (
    <>
        <SuperText text={'1'} color={'red'}/> This is a target not met (TNM) profile therefore the local WSEL is
        interpolated.
        <br/>
        <SuperText text={'2'} color={'red'}/> This is a choking cross section in this profile therefore the local WSEL
        value is adjusted.
        <br/>
        <SuperText text={'3'} color={'red'}/> This is a crossing profile at this cross section therefore the local
        <br/>
        <SuperText text={'4'} color={'red'}/> Structure is not on the same reach with the gage.
        WSEL value is adjusted.
        <EmptyLine />
        <b>Issue Messages</b>
        <br/>
        <table>
            <tr>
                <td style={{width: 60}}>Issue 1</td>
                <td>Minimum Local WSEL is too high and results in a freeboard less than 2 ft, therefore
                    a Normal inundation threshold could not be identified.
                </td>
            </tr>
            <tr>
                <td>Issue 2</td>
                <td>Minimum Local WSEL is too high and results in a freeboard less than 0 ft, therefore a Normal or Warning inundation threshold could not be identified.
                </td>
            </tr>
            <tr>
                <td>Issue 3</td>
                <td>Maximum Local WSEL is too low and results in a freeboard more than 2 ft, therefore a Warning or Pressure/Weir inundation threshold could not be identified.
                </td>
            </tr>
            <tr>
                <td>Issue 4</td>
                <td>Maximum Local WSEL is low and results in a freeboard more than 0 ft, therefore a Pressure/Weir inundation threshold could not be identified.
                </td>
            </tr>
        </table>
    </>
)

export function G7({data, message_key}) {
    const {settings} = useUser()

    return (
        <>
            {
                data?.result?.map(row => (
                    <ResultView error_id={row?.error_id} message_key={message_key}>
                        <Description row={row} settings={settings} globalVariables={data?.global_variables} />
                    </ResultView>
                ))
            }
        </>
    )
}
const Description = ({text, row, settings, globalVariables}) => {
    switch (row.error_id) {
        case MessageCodes.Internal.LB07.error_id: {
            return (
                <>
                    <ResultText>
                        The inundation thresholds table for total {row?.data?.length} (bridge and culvert) structures in the library model is noted below. The gage is located at RS {row?.gage_station}. The local WSEL is calculated at the US cross section for culverts and at the US Internal Bridge cross section for bridges. High Chord Elevation is set as the lowest High Chord elevation within the span of the structure. Low Chord Elevation is set as the lowest Low Chord elevation within the span of the structure.
                        <br />
                        a.	For bridges, the Warning Profile results in a local WSEL (noted within parentheses) with less than 2 ft freeboard relative to the lowest Low Chord Elevation of the structure. The Weir/Pressure Profile results in a local WSEL equal to or higher than lowest Low Chord Elevation.
                        <br/>
                        b.	For culverts, the Warning Profile results in a local WSEL (noted within parentheses) with less than 2 ft freeboard relative to the lowest High Chord Elevation of the structure. The Overtopping Profile results in a local WSEL equal to or higher than lowest High Chord Elevation.
                    </ResultText>
                    <EmptyLine />
                    <ResultTable style={{width: HecRasHelper.getWidth(123)}}  data={row}>
                        <ResultHeader>
                            <ResultRow>
                                <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'left',  width: HecRasHelper.getWidth(15)}}>River Station</ResultHeaderCell>
                                <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'left',  width: HecRasHelper.getWidth(12)}}>Structure Type</ResultHeaderCell>
                                <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'left',  width: HecRasHelper.getWidth(20)}}>Structure Description</ResultHeaderCell>
                                <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'right', width: HecRasHelper.getWidth(12)}}>Low. High Chord Elev (ft)</ResultHeaderCell>
                                <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'right', width: HecRasHelper.getWidth(12)}}>Low. Low Chord Elev (ft)</ResultHeaderCell>
                                <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'right', width: HecRasHelper.getWidth(12)}}>Distance to Gage (ft)</ResultHeaderCell>
                                <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'right', width: HecRasHelper.getWidth(16)}}>Warning Profile (Loc WSEL) ft</ResultHeaderCell>
                                <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'right', width: HecRasHelper.getWidth(24)}}>Weir/Pressure Profile (Loc WSEL) ft</ResultHeaderCell>
                            </ResultRow>
                        </ResultHeader>
                        <ResultBody>
                            {
                                row?.data?.map(x => {
                                    const getWarning = () => {
                                        if(x.warning_issue)
                                            return (<span style={{color: 'red'}}>Issue {x.warning_issue}</span>)

                                        if(x.warning_item) {
                                            if(x.footnote) {
                                                return (
                                                    (<span><SuperText text={'1'} color={'red'}/>{x.warning_item.profile} ({x.warning_item.loc_wsel})</span>)
                                                )
                                            }

                                            return (
                                                (<span>{x.warning_item.profile} ({x.warning_item.loc_wsel})</span>)
                                            )
                                        }

                                        return <span style={{color: 'red'}}>-</span>
                                    }

                                    const getPressure = () => {
                                        if(x.weir_pressure_issue)
                                            return (<span style={{color: 'red'}}>Issue {x.weir_pressure_issue}</span>)

                                        if(x.weir_pressure_item) {
                                            if(x.footnote) {
                                                return (
                                                    (<span><SuperText text={'1'} color={'red'}/>{x.weir_pressure_item.profile} ({x.weir_pressure_item.loc_wsel})</span>)
                                                )
                                            }

                                            return (
                                                (<span>{x.weir_pressure_item.profile} ({x.weir_pressure_item.loc_wsel})</span>)
                                            )
                                        }

                                        return <span style={{color: 'red'}}>-</span>
                                    }

                                    return (
                                        <ResultRow>
                                            <ResultBodyCell style={{textAlign: 'left',  width: HecRasHelper.getWidth(15)}}>
                                                <CheckCSName csName={x.cross_section} />
                                            </ResultBodyCell>
                                            <ResultBodyCell style={{textAlign: 'left',  width: HecRasHelper.getWidth(12)}}>{x.type}</ResultBodyCell>
                                            <ResultBodyCell style={{textAlign: 'left',  width: HecRasHelper.getWidth(20)}}>{x.description}</ResultBodyCell>
                                            <ResultBodyCell style={{textAlign: 'right', width: HecRasHelper.getWidth(12)}}>{x.min_high_chord}</ResultBodyCell>
                                            <ResultBodyCell style={{textAlign: 'right', width: HecRasHelper.getWidth(12)}}>{parseFloat(x.min_low_chord)?.toFixed(2)}</ResultBodyCell>
                                            <ResultBodyCell style={{textAlign: 'right', width: HecRasHelper.getWidth(12)}}>{x.gage_dist} {x?.gage_dist_ft && (<SuperText text={x?.gage_dist_ft} color={'red'}/>)} </ResultBodyCell>
                                            <ResultBodyCell style={{textAlign: 'right', width: HecRasHelper.getWidth(16)}}>{getWarning()}</ResultBodyCell>
                                            <ResultBodyCell style={{textAlign: 'right', width: HecRasHelper.getWidth(24)}}>{getPressure()}</ResultBodyCell>
                                        </ResultRow>
                                    )
                                })
                            }
                        </ResultBody>
                    </ResultTable>
                    <EmptyLine />
                    <div style={{fontSize: '8pt'}}>
                        <Footnote />
                    </div>
                </>
            )
        }
        case MessageCodes.Internal.LB08.error_id: {
            return (
                <>
                    <ResultText>
                        The individual inundation table for each structure is noted below.
                    </ResultText>
                    <EmptyLine />
                    {
                        row?.data?.map(r => renderLB08({rs: r}))
                    }
                </>
            )
        }
        default: {
            return `Error Key Not Found: ${row.error_id}`
        }
    }
}

const renderLB08 = ({rs}) => {
    const printStatus = ({status}) => {
        if(status === 'Warning') {
            return <span style={{color: 'orange'}}>{status}</span>
        }
        else if(status === 'Pressure/Weir') {
            return <span style={{color: 'red'}}>{status}</span>
        }
        else {
            return <span style={{color: ''}}>Normal</span>
        }
    }

    return (
        <>
            <ResultTable style={{width: HecRasHelper.getWidth(109)}}>
                <ResultHeader>
                    <ResultRow>
                        <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'left', width: HecRasHelper.getWidth(15)}}>River Station</ResultHeaderCell>
                        <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'left', width: HecRasHelper.getWidth(12)}}>Structure Type</ResultHeaderCell>
                        <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'right', width: HecRasHelper.getWidth(15)}}>Low. High Chord Elev (ft)</ResultHeaderCell>
                        <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'right', width: HecRasHelper.getWidth(15)}}>Low. Low Chord Elev (ft)</ResultHeaderCell>
                        <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'right', width: HecRasHelper.getWidth(10)}}>Profile</ResultHeaderCell>
                        <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'right', width: HecRasHelper.getWidth(10)}}>Loc. WSEL (ft)</ResultHeaderCell>
                        <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'right', width: HecRasHelper.getWidth(10)}}>Freeboard (ft)</ResultHeaderCell>
                        <ResultHeaderCell style={{verticalAlign: 'top', textAlign: 'right', width: HecRasHelper.getWidth(22)}}>Status</ResultHeaderCell>
                    </ResultRow>
                </ResultHeader>
                <ResultBody>
                    {
                        rs?.profiles?.map(x => {
                            return (
                                <ResultRow>
                                    <ResultBodyCell style={{textAlign: 'left', width: HecRasHelper.getWidth(15)}}>
                                        <CheckCSName csName={rs.cross_section} />
                                    </ResultBodyCell>
                                    <ResultBodyCell style={{textAlign: 'left', width: HecRasHelper.getWidth(12)}}>{rs.type}</ResultBodyCell>
                                    <ResultBodyCell style={{textAlign: 'right', width: HecRasHelper.getWidth(15)}}>{rs.min_high_chord}</ResultBodyCell>
                                    <ResultBodyCell style={{textAlign: 'right', width: HecRasHelper.getWidth(15)}}>{parseFloat(rs.min_low_chord)?.toFixed(2)}</ResultBodyCell>
                                    <ResultBodyCell style={{textAlign: 'right', width: HecRasHelper.getWidth(10)}}>{x.profile}</ResultBodyCell>
                                    <ResultBodyCell style={{textAlign: 'right', width: HecRasHelper.getWidth(10)}}>{x.loc_wsel}</ResultBodyCell>
                                    <ResultBodyCell style={{textAlign: 'right', width: HecRasHelper.getWidth(10)}}>{x.freeboard !== '-' ? x.freeboard?.toFixed(2) : '-'}</ResultBodyCell>
                                    <ResultBodyCell style={{textAlign: 'right', width: HecRasHelper.getWidth(22)}}>{printStatus({status: x.status})}</ResultBodyCell>
                                </ResultRow>
                            )
                        })
                    }
                </ResultBody>
            </ResultTable>
            <EmptyLine />
        </>
    )
}

const StaElevPlot = ({riverStation, globalVariables, layers}) => {

    return (
        <>
            <StationElevationPlot
                locWselEnable={true}
                crossSection={`${riverStation?.cross_section} Upstream (${riverStation?.type})`}
                plotData={{
                    base: riverStation?.plot_us,
                }}
                globalVariables={globalVariables}
                layers={layers}
            />
        </>
    )
}